import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useQuotes } from '../contexts/QuotesContext';
import QuoteDetailModal from '../components/QuoteDetailModal';
import { useLanguage } from '../i18n/LanguageContext';

const DashboardContainer = styled.div`
  min-height: 100vh;
  background: #f5f7fa;
`;

const Header = styled.header`
  background: white;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  padding: 16px 40px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Logo = styled.h1`
  font-size: 24px;
  color: #0c4da2;
  margin: 0;
`;

const UserSection = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
`;

const SettingsButton = styled.button`
  background: transparent;
  color: #4a5568;
  border: 1px solid #cbd5e0;
  padding: 8px 20px;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 600;
  transition: all 0.2s ease;

  &:hover {
    background: #edf2f7;
    color: #2d3748;
  }
`;

const UserInfo = styled.span`
  font-size: 14px;
  color: #666;
`;

const LogoutButton = styled.button`
  background: #e53e3e;
  color: white;
  border: none;
  padding: 8px 20px;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 600;
  transition: all 0.2s ease;

  &:hover {
    background: #c53030;
    transform: translateY(-1px);
  }
`;

const MainContent = styled.main`
  max-width: 1400px;
  margin: 0 auto;
  padding: 40px 20px;
`;

const PageTitle = styled.h2`
  font-size: 32px;
  color: #1a202c;
  margin-bottom: 10px;
`;

const PageSubtitle = styled.p`
  font-size: 16px;
  color: #718096;
  margin-bottom: 30px;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 20px;
  margin-bottom: 40px;
`;

const StatCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
`;

const StatLabel = styled.div`
  font-size: 14px;
  color: #718096;
  margin-bottom: 8px;
  font-weight: 500;
`;

const StatValue = styled.div`
  font-size: 36px;
  font-weight: 700;
  color: ${props => props.color || '#1a202c'};
`;

const FilterSection = styled.div`
  background: white;
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 20px;
  display: flex;
  gap: 15px;
  align-items: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
`;

const FilterLabel = styled.label`
  font-size: 14px;
  font-weight: 600;
  color: #4a5568;
`;

const Select = styled.select`
  padding: 8px 12px;
  border: 2px solid #e2e8f0;
  border-radius: 6px;
  font-size: 14px;
  cursor: pointer;
  
  &:focus {
    outline: none;
    border-color: #0c4da2;
  }
`;

const QuotesTable = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  overflow: hidden;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Thead = styled.thead`
  background: #f7fafc;
  border-bottom: 2px solid #e2e8f0;
`;

const Th = styled.th`
  text-align: left;
  padding: 16px 20px;
  font-size: 14px;
  font-weight: 600;
  color: #4a5568;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const Td = styled.td`
  padding: 16px 20px;
  border-bottom: 1px solid #e2e8f0;
  font-size: 14px;
  color: #2d3748;
`;

const ClickableRow = styled.tr`
  cursor: pointer;
  transition: background 0.2s;

  &:hover {
    background: #f7fafc;
  }
`;

const StatusBadge = styled.span`
  display: inline-block;
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  
  ${props => {
    switch (props.$status) {
      case 'pending':
        return 'background: #fef5e7; color: #f39c12;';
      case 'processed':
        return 'background: #d4edda; color: #28a745;';
      case 'rejected':
        return 'background: #f8d7da; color: #dc3545;';
      default:
        return 'background: #e2e8f0; color: #718096;';
    }
  }}
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 8px;
`;

const ActionButton = styled.button`
  padding: 6px 12px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
  font-weight: 600;
  transition: all 0.2s ease;
  
  ${props => {
    if (props.$variant === 'approve') {
      return `
        background: #28a745;
        color: white;
        &:hover { background: #218838; }
      `;
    } else if (props.$variant === 'reject') {
      return `
        background: #dc3545;
        color: white;
        &:hover { background: #c82333; }
      `;
    } else {
      return `
        background: #6c757d;
        color: white;
        &:hover { background: #5a6268; }
      `;
    }
  }}
`;

const EmptyState = styled.div`
  padding: 60px 20px;
  text-align: center;
  color: #718096;
`;

const AdminDashboard = () => {
  const { t, locale } = useLanguage();
  const { logout } = useAuth();
  const { quotes, fetchQuotes, deleteQuote, loading } = useQuotes();
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedQuote, setSelectedQuote] = useState(null);

  useEffect(() => {
    // Fetch/re-fetch when filter changes
    if (statusFilter !== 'all') {
      fetchQuotes({ status: statusFilter });
    } else {
      fetchQuotes();
    }
  }, [fetchQuotes, statusFilter]);

  const handleLogout = async () => {
    await logout();
    navigate('/admin/login');
  };

  const handleDelete = async (quoteId) => {
    if (window.confirm(t('pages.adminDashboard.alerts.deleteConfirm'))) {
      const result = await deleteQuote(quoteId);
      if (!result.success) {
        alert(t('pages.adminDashboard.alerts.deleteError'));
      }
    }
  };

  const handleViewDetails = (quote) => {
    setSelectedQuote(quote);
  };

  const handleCloseModal = () => {
    setSelectedQuote(null);
  };

  const handleQuoteUpdate = (updatedQuote) => {
    setSelectedQuote(null);
    fetchQuotes(); // Refresh the list
  };

  const filteredQuotes = statusFilter === 'all' 
    ? quotes 
    : quotes.filter(q => q.status === statusFilter);

  const stats = {
    total: quotes.length,
    pending: quotes.filter(q => q.status === 'pending').length,
    processed: quotes.filter(q => q.status === 'processed').length,
    rejected: quotes.filter(q => q.status === 'rejected').length,
  };

  const formatDate = (isoString) => {
    const date = new Date(isoString);
    return date.toLocaleDateString(locale || 'fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusLabel = (status) => {
    const labels = t('pages.adminDashboard.statusLabels');
    return labels[status] || status;
  };

  return (
    <DashboardContainer>
      <Header>
        <Logo>{t('pages.adminDashboard.logo')}</Logo>
        <UserSection>
          <UserInfo>👤 {t('pages.adminDashboard.userLabel')}</UserInfo>
          <SettingsButton onClick={() => navigate('/admin/settings')}>
            ⚙️ {t('pages.adminDashboard.settings')}
          </SettingsButton>
          <LogoutButton onClick={handleLogout}>{t('pages.adminDashboard.logout')}</LogoutButton>
        </UserSection>
      </Header>

      <MainContent>
        <PageTitle>{t('pages.adminDashboard.title')}</PageTitle>
        <PageSubtitle>{t('pages.adminDashboard.subtitle')}</PageSubtitle>

        <StatsGrid>
          <StatCard>
            <StatLabel>{t('pages.adminDashboard.stats.total')}</StatLabel>
            <StatValue>{stats.total}</StatValue>
          </StatCard>
          <StatCard>
            <StatLabel>{t('pages.adminDashboard.stats.pending')}</StatLabel>
            <StatValue color="#f39c12">{stats.pending}</StatValue>
          </StatCard>
          <StatCard>
            <StatLabel>{t('pages.adminDashboard.stats.processed')}</StatLabel>
            <StatValue color="#28a745">{stats.processed}</StatValue>
          </StatCard>
          <StatCard>
            <StatLabel>{t('pages.adminDashboard.stats.rejected')}</StatLabel>
            <StatValue color="#dc3545">{stats.rejected}</StatValue>
          </StatCard>
        </StatsGrid>

        <FilterSection>
          <FilterLabel>{t('pages.adminDashboard.filterLabel')}</FilterLabel>
          <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="all">{t('pages.adminDashboard.filterOptions.all')}</option>
            <option value="pending">{t('pages.adminDashboard.filterOptions.pending')}</option>
            <option value="processed">{t('pages.adminDashboard.filterOptions.processed')}</option>
            <option value="rejected">{t('pages.adminDashboard.filterOptions.rejected')}</option>
          </Select>
        </FilterSection>

        <QuotesTable>
          {loading ? (
            <EmptyState>
              <h3>{t('pages.adminDashboard.empty.loading')}</h3>
            </EmptyState>
          ) : filteredQuotes.length === 0 ? (
            <EmptyState>
              <h3>{t('pages.adminDashboard.empty.title')}</h3>
              <p>{t('pages.adminDashboard.empty.subtitle')}</p>
            </EmptyState>
          ) : (
            <Table>
              <Thead>
                <tr>
                  <Th>{t('pages.adminDashboard.table.date')}</Th>
                  <Th>{t('pages.adminDashboard.table.contact')}</Th>
                  <Th>{t('pages.adminDashboard.table.email')}</Th>
                  <Th>{t('pages.adminDashboard.table.route')}</Th>
                  <Th>{t('pages.adminDashboard.table.status')}</Th>
                  <Th>{t('pages.adminDashboard.table.actions')}</Th>
                </tr>
              </Thead>
              <tbody>
                {filteredQuotes.map((quote) => (
                  <ClickableRow key={quote.id} onClick={() => handleViewDetails(quote)}>
                    <Td>{formatDate(quote.createdAt)}</Td>
                    <Td>{quote.name}</Td>
                    <Td>{quote.email}</Td>
                    <Td>
                      <strong>{quote.origin}</strong> → <strong>{quote.destination}</strong>
                      {quote.details && (
                        <div style={{ fontSize: '12px', color: '#718096', marginTop: '4px' }}>
                          {quote.details.substring(0, 50)}{quote.details.length > 50 ? '...' : ''}
                        </div>
                      )}
                    </Td>
                    <Td>
                      <StatusBadge $status={quote.status}>
                        {getStatusLabel(quote.status)}
                      </StatusBadge>
                    </Td>
                    <Td onClick={(e) => e.stopPropagation()}>
                      <ActionButtons>
                        <ActionButton 
                          $variant="approve"
                          onClick={() => handleViewDetails(quote)}
                        >
                          👁️ {t('pages.adminDashboard.table.viewDetails')}
                        </ActionButton>
                        <ActionButton 
                          $variant="delete"
                          onClick={() => handleDelete(quote.id)}
                        >
                          🗑
                        </ActionButton>
                      </ActionButtons>
                    </Td>
                  </ClickableRow>
                ))}
              </tbody>
            </Table>
          )}
        </QuotesTable>
      </MainContent>

      {selectedQuote && (
        <QuoteDetailModal
          quote={selectedQuote}
          onClose={handleCloseModal}
          onUpdate={handleQuoteUpdate}
        />
      )}
    </DashboardContainer>
  );
};

export default AdminDashboard;
